/**
 * JSON Viewer Application Entry Point
 * 
 * This is the main entry point for the JSON Viewer application.
 * It initializes the application and sets up the basic structure.
 */

// Import styles
import './styles/main.css'

// Import services and utilities
import { JSONValidator } from './services/JSONValidator.js'
import { ValidationFeedback } from './utils/ValidationFeedback.js'
import { JSONInput } from './components/JSONInput.js'
import { JSONTreeViewer } from './components/JSONTreeViewer.js'
import { KeyboardHelpModal } from './components/KeyboardHelpModal.js'
import { FullscreenManager } from './utils/FullscreenManager.js'
import { URLParameterHandler } from './utils/URLParameterHandler.js'
import { FeedbackButton } from './components/FeedbackButton.js'
import { MobileTabManager } from './utils/MobileTabManager.js'

// Application initialization
class JSONViewerApp {
    constructor() {
        this.validationFeedback = null
        this.jsonInput = null
        this.treeViewer = null
        this.keyboardHelpModal = null
        this.fullscreenManager = null
        this.urlParameterHandler = null
        this.mobileTabManager = null
        this.feedbackButton = null
        this.isLoadingSharedJSON = false
        this.init()
    }

    init() {
        console.log('JSON Viewer Application initialized')

        // Basic DOM ready check
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => {
                this.setupApplication()
            })
        } else {
            this.setupApplication()
        }
    }

    async setupApplication() {
        console.log('Setting up JSON Viewer application...')

        // Application setup will be expanded in future tasks
        // For now, just ensure the basic structure is in place
        const app = document.getElementById('app')
        if (!app) {
            console.error('Application container not found')
            return
        }

        // Set up components first so they exist when we handle shared JSON
        this.setupJSONInput()
        this.setupTreeViewer()

        // Set up fullscreen functionality
        this.setupFullscreen()

        // Set up keyboard help modal
        this.setupKeyboardHelpModal()

        // Set up mobile tab functionality
        this.setupMobileTabs()

        // Set up feedback system
        this.setupFeedback()

        // Check for shared JSON in URL parameters after components are ready
        await this.handleSharedJSON()

        console.log('JSON Viewer application ready')
    }

    /**
     * Handle shared JSON loading from URL parameters
     */
    async handleSharedJSON() {
        try {
            // Check for sample loading first
            const urlParams = new URLSearchParams(window.location.search)
            const sampleType = urlParams.get('sample')

            if (sampleType) {
                await this.loadSampleData(sampleType)
                return
            }

            // Check if there's a shared JSON ID in the URL
            const sharedId = URLParameterHandler.parseSharedId()

            if (!sharedId) {
                return // No shared JSON to load
            }

            console.log('Found shared JSON ID in URL:', sharedId)

            // Validate the share ID format
            if (!URLParameterHandler.isValidShareId(sharedId)) {
                console.warn('Invalid share ID format:', sharedId)
                this.showSharedJSONError('Invalid share link format')
                return
            }

            // Show loading state
            this.showSharedJSONLoading(true)

            // Show loading in both columns
            if (this.jsonInput) {
                this.jsonInput.showLoading('Loading shared JSON...')
            }

            if (this.treeViewer) {
                this.treeViewer.showLoading('Loading shared JSON...')
            }

            // Initialize URL parameter handler
            this.urlParameterHandler = new URLParameterHandler({
                onLoadStart: (shareId) => {
                    console.log('Loading shared JSON:', shareId)
                },
                onLoadSuccess: (result) => {
                    console.log('Shared JSON loaded successfully:', result)
                },
                onLoadError: (error) => {
                    console.error('Failed to load shared JSON:', error)
                },
                onLoadComplete: (shareId) => {
                    this.showSharedJSONLoading(false)

                    // Hide loading from both columns
                    if (this.jsonInput) {
                        this.jsonInput.hideLoading()
                    }

                    if (this.treeViewer) {
                        this.treeViewer.hideLoading()
                    }
                }
            })

            // Load the shared JSON
            const result = await this.urlParameterHandler.loadSharedJSON(sharedId)

            if (result.success && result.jsonData) {
                // Successfully loaded shared JSON
                this.loadSharedJSONData(result.jsonData, result.metadata)
                this.showSharedJSONSuccess()

                // Hide loading in tree viewer
                if (this.treeViewer) {
                    this.treeViewer.hideLoading()
                }
            } else {
                // Failed to load shared JSON
                this.showSharedJSONError(result.error || 'Failed to load shared JSON')

                // Hide loading in tree viewer
                if (this.treeViewer) {
                    this.treeViewer.hideLoading()
                }
            }

        } catch (error) {
            console.error('Error handling shared JSON:', error)
            this.showSharedJSONError(error.message || 'An unexpected error occurred while loading shared JSON')
            this.showSharedJSONLoading(false)

            // Hide loading in tree viewer
            if (this.treeViewer) {
                this.treeViewer.hideLoading()
            }
        }
    }

    /**
     * Load sample data from sessionStorage or generate it
     * @param {string} sampleType - Type of sample to load
     */
    async loadSampleData(sampleType) {
        try {
            console.log('Loading sample data:', sampleType)

            // First try to get from sessionStorage (set by samples page)
            let sampleData = sessionStorage.getItem(`sample_${sampleType}`)

            if (sampleData) {
                // Clean up sessionStorage
                sessionStorage.removeItem(`sample_${sampleType}`)

                // Load the sample data
                this.loadSharedJSONData(sampleData)
                this.showSampleLoadedMessage(sampleType)
            } else {
                // If no data in sessionStorage, try to generate it
                // This requires importing the sample generators
                try {
                    const { generateSample } = await import('./js/samples.js')
                    const generatedData = generateSample(sampleType)
                    const jsonString = JSON.stringify(generatedData, null, 2)

                    this.loadSharedJSONData(jsonString)
                    this.showSampleLoadedMessage(sampleType)
                } catch (importError) {
                    console.error('Could not generate sample data:', importError)
                    this.showSampleError(sampleType)
                }
            }

            // Clean up URL parameter
            const url = new URL(window.location)
            url.searchParams.delete('sample')
            window.history.replaceState(null, '', url.toString())

        } catch (error) {
            console.error('Error loading sample data:', error)
            this.showSampleError(sampleType)
        }
    }

    /**
     * Show a message that a sample was loaded
     * @param {string} sampleType - Type of sample that was loaded
     */
    showSampleLoadedMessage(sampleType) {
        // Create a temporary message element
        const message = document.createElement('div')
        message.className = 'sample-loaded-message'
        message.textContent = `Sample "${sampleType}" loaded successfully`
        message.style.cssText = `
            position: fixed;
            top: 20px;
            right: 20px;
            background: #10b981;
            color: white;
            padding: 12px 24px;
            border-radius: 6px;
            font-weight: 500;
            z-index: 1000;
            transform: translateX(100%);
            transition: transform 0.3s ease;
        `

        document.body.appendChild(message)

        // Animate in
        setTimeout(() => {
            message.style.transform = 'translateX(0)'
        }, 100)

        // Remove after 3 seconds
        setTimeout(() => {
            message.style.transform = 'translateX(100%)'
            setTimeout(() => {
                if (message.parentNode) {
                    message.parentNode.removeChild(message)
                }
            }, 300)
        }, 3000)
    }

    /**
     * Show an error message for sample loading
     * @param {string} sampleType - Type of sample that failed to load
     */
    showSampleError(sampleType) {
        const message = document.createElement('div')
        message.className = 'sample-error-message'
        message.textContent = `Failed to load sample "${sampleType}"`
        message.style.cssText = `
            position: fixed;
            top: 20px;
            right: 20px;
            background: #ef4444;
            color: white;
            padding: 12px 24px;
            border-radius: 6px;
            font-weight: 500;
            z-index: 1000;
            transform: translateX(100%);
            transition: transform 0.3s ease;
        `

        document.body.appendChild(message)

        // Animate in
        setTimeout(() => {
            message.style.transform = 'translateX(0)'
        }, 100)

        // Remove after 3 seconds
        setTimeout(() => {
            message.style.transform = 'translateX(100%)'
            setTimeout(() => {
                if (message.parentNode) {
                    message.parentNode.removeChild(message)
                }
            }, 300)
        }, 3000)
    }

    /**
     * Load shared JSON data into the application
     */
    loadSharedJSONData(jsonData, metadata = {}) {
        try {
            // If the input component already exists, load the data directly
            if (this.jsonInput) {
                console.log('Loading shared JSON directly into existing input component')
                this.jsonInput.setValue(jsonData)
            } else {
                // Store the shared JSON data to be loaded when the input component is created
                this.sharedJSONData = {
                    data: jsonData,
                    metadata: metadata,
                    loadedAt: new Date().toISOString()
                }

                console.log('Shared JSON data prepared for loading:', {
                    dataLength: jsonData.length,
                    metadata: metadata
                })
            }

        } catch (error) {
            console.error('Error preparing shared JSON data:', error)
            throw new Error('Failed to prepare shared JSON data')
        }
    }

    /**
     * Check if there's a shared URL parameter that should take precedence
     */
    hasSharedUrlParameter() {
        const urlParams = new URLSearchParams(window.location.search)
        return urlParams.has('share') || urlParams.has('sample')
    }

    /**
     * Show loading state for shared JSON
     */
    showSharedJSONLoading(isLoading) {
        this.isLoadingSharedJSON = isLoading

        if (isLoading) {
            // Show loading indicator
            this.showNotification('Loading shared JSON...', 'info', 0) // 0 = no auto-hide
        } else {
            // Hide loading indicator
            this.hideNotification()
        }
    }

    /**
     * Show success message for shared JSON loading
     */
    showSharedJSONSuccess() {
        this.showNotification('Shared JSON loaded successfully!', 'success', 3000)
    }

    /**
     * Show error message for shared JSON loading
     */
    showSharedJSONError(message) {
        const errorMessage = `Failed to load shared JSON: ${message}`
        this.showNotification(errorMessage, 'error', 8000)

        // Clear the share parameter from URL since it's invalid
        URLParameterHandler.clearShareParameter()
    }

    /**
     * Show notification to user
     */
    showNotification(message, type = 'info', autoHideMs = 5000) {
        // Remove existing notification
        this.hideNotification()

        // Create notification element
        const notification = document.createElement('div')
        notification.className = `app-notification app-notification-${type}`
        notification.innerHTML = `
            <div class="notification-content">
                <span class="notification-icon">${this.getNotificationIcon(type)}</span>
                <span class="notification-message">${this.escapeHtml(message)}</span>
                <button type="button" class="notification-close" aria-label="Close notification">✕</button>
            </div>
        `

        // Add to document
        document.body.appendChild(notification)

        // Setup close button
        const closeBtn = notification.querySelector('.notification-close')
        closeBtn.addEventListener('click', () => this.hideNotification())

        // Auto-hide if specified
        if (autoHideMs > 0) {
            setTimeout(() => this.hideNotification(), autoHideMs)
        }

        // Store reference
        this.currentNotification = notification
    }

    /**
     * Hide current notification
     */
    hideNotification() {
        if (this.currentNotification) {
            this.currentNotification.remove()
            this.currentNotification = null
        }
    }

    /**
     * Get icon for notification type
     */
    getNotificationIcon(type) {
        switch (type) {
            case 'success': return '✅'
            case 'error': return '❌'
            case 'warning': return '⚠️'
            case 'info':
            default: return 'ℹ️'
        }
    }

    /**
     * Escape HTML to prevent XSS
     */
    escapeHtml(text) {
        const div = document.createElement('div')
        div.textContent = text
        return div.innerHTML
    }

    /**
     * Set up JSON input component
     */
    setupJSONInput() {
        const inputContainer = document.querySelector('.json-input-container')

        if (inputContainer) {
            console.log('Setting up JSON input component...')

            // Clear existing content
            inputContainer.innerHTML = ''

            // Initialize JSON input component
            this.jsonInput = new JSONInput(inputContainer, {
                placeholder: 'Paste your JSON here to validate and view...',
                rows: 15,
                debounceDelay: 300,
                showSuggestions: true,
                enableSyntaxHighlighting: true,
                onValidJSON: async (data, result) => {
                    console.log('Valid JSON received:', data)
                    await this.updateTreeViewer(data)
                },
                onInvalidJSON: (result) => {
                    console.log('Invalid JSON:', result)
                    this.clearTreeViewer()
                },
                onChange: (value) => {
                    // Future: Handle real-time updates
                }
            })

            // Load shared JSON data if available, otherwise set sample JSON
            if (this.sharedJSONData && this.sharedJSONData.data) {
                console.log('Loading shared JSON into input component')
                this.jsonInput.setValue(this.sharedJSONData.data)

                // Clear the shared data after loading
                this.sharedJSONData = null
            } else if (!this.jsonInput.getValue().trim() && !this.hasSharedUrlParameter()) {
                const sampleJSON = {
                    "application": {
                        "name": "JSONly Viewer 123",
                        "version": "2.1.4",
                        "description": "A comprehensive JSON visualization and editing tool with advanced features",
                        "author": {
                            "name": "Development Team",
                            "email": "dev@jsonviewer.com",
                            "organization": "JSON Tools Inc.",
                            "website": "https://jsonviewer.com"
                        },
                        "license": "MIT",
                        "repository": {
                            "type": "git",
                            "url": "https://github.com/jsontools/json-viewer",
                            "branch": "main",
                            "lastCommit": "a7b3c9d",
                            "contributors": 15
                        }
                    },
                    "features": {
                        "core": [
                            "Real-time JSON validation",
                            "Syntax highlighting with themes",
                            "JSON beautification and minification",
                            "Advanced error reporting with line numbers",
                            "Intelligent search and replace functionality",
                            "Local storage persistence",
                            "Import/Export capabilities",
                            "Collapsible tree view navigation"
                        ],
                        "advanced": [
                            "Schema validation support",
                            "JSONPath query execution",
                            "Diff comparison between JSON files",
                            "Custom formatting rules",
                            "Plugin architecture",
                            "API integration",
                            "Batch processing",
                            "Performance monitoring"
                        ],
                        "ui": {
                            "themes": ["light", "dark", "high-contrast", "solarized"],
                            "layouts": ["split-view", "fullscreen", "tabbed", "floating"],
                            "accessibility": {
                                "screenReaderSupport": true,
                                "keyboardNavigation": true,
                                "highContrastMode": true,
                                "fontSize": "adjustable"
                            }
                        }
                    },
                    "configuration": {
                        "editor": {
                            "theme": "dark",
                            "fontSize": 14,
                            "fontFamily": "Monaco, Consolas, monospace",
                            "tabSize": 2,
                            "wordWrap": true,
                            "lineNumbers": true,
                            "autoIndent": true,
                            "bracketMatching": true,
                            "autoComplete": {
                                "enabled": true,
                                "triggerCharacters": ["{", "[", "\"", ":"],
                                "suggestions": ["string", "number", "boolean", "null", "object", "array"]
                            }
                        },
                        "validation": {
                            "realTime": true,
                            "strictMode": false,
                            "allowComments": true,
                            "allowTrailingCommas": true,
                            "maxDepth": 100,
                            "maxSize": "10MB"
                        },
                        "performance": {
                            "debounceDelay": 300,
                            "virtualScrolling": true,
                            "lazyLoading": true,
                            "cacheResults": true,
                            "compressionEnabled": true
                        }
                    },
                    "sampleData": {
                        "users": [
                            {
                                "id": 1001,
                                "username": "john_doe",
                                "email": "john.doe@example.com",
                                "profile": {
                                    "firstName": "John",
                                    "lastName": "Doe",
                                    "age": 32,
                                    "avatar": "https://api.example.com/avatars/john_doe.jpg",
                                    "bio": "Senior software engineer with 8+ years of experience in web development",
                                    "location": {
                                        "city": "San Francisco",
                                        "state": "California",
                                        "country": "USA",
                                        "timezone": "PST",
                                        "coordinates": {
                                            "lat": 37.7749,
                                            "lng": -122.4194
                                        }
                                    }
                                },
                                "preferences": {
                                    "theme": "dark",
                                    "notifications": {
                                        "email": true,
                                        "push": false,
                                        "sms": true
                                    },
                                    "privacy": {
                                        "profileVisibility": "public",
                                        "showEmail": false,
                                        "allowMessages": true
                                    }
                                },
                                "roles": ["admin", "developer", "reviewer"],
                                "permissions": {
                                    "read": ["*"],
                                    "write": ["projects", "users", "settings"],
                                    "delete": ["projects"]
                                },
                                "activity": {
                                    "lastLogin": "2025-01-08T14:30:00Z",
                                    "loginCount": 1247,
                                    "createdAt": "2020-03-15T09:00:00Z",
                                    "updatedAt": "2025-01-08T14:30:00Z"
                                },
                                "isActive": true,
                                "isVerified": true
                            },
                            {
                                "id": 1002,
                                "username": "jane_smith",
                                "email": "jane.smith@example.com",
                                "profile": {
                                    "firstName": "Jane",
                                    "lastName": "Smith",
                                    "age": 28,
                                    "avatar": "https://api.example.com/avatars/jane_smith.jpg",
                                    "bio": "UX/UI designer passionate about creating intuitive user experiences",
                                    "location": {
                                        "city": "New York",
                                        "state": "New York",
                                        "country": "USA",
                                        "timezone": "EST",
                                        "coordinates": {
                                            "lat": 40.7128,
                                            "lng": -74.0060
                                        }
                                    }
                                },
                                "preferences": {
                                    "theme": "light",
                                    "notifications": {
                                        "email": false,
                                        "push": true,
                                        "sms": false
                                    },
                                    "privacy": {
                                        "profileVisibility": "private",
                                        "showEmail": false,
                                        "allowMessages": false
                                    }
                                },
                                "roles": ["designer", "user"],
                                "permissions": {
                                    "read": ["projects", "users"],
                                    "write": ["projects"],
                                    "delete": []
                                },
                                "activity": {
                                    "lastLogin": "2025-01-07T16:45:00Z",
                                    "loginCount": 892,
                                    "createdAt": "2021-07-22T11:30:00Z",
                                    "updatedAt": "2025-01-07T16:45:00Z"
                                },
                                "isActive": true,
                                "isVerified": true
                            }
                        ],
                        "projects": [
                            {
                                "id": "proj_001",
                                "name": "E-commerce Platform Redesign",
                                "description": "Complete overhaul of the existing e-commerce platform with modern UI/UX",
                                "status": "in-progress",
                                "priority": "high",
                                "team": {
                                    "lead": 1001,
                                    "members": [1001, 1002],
                                    "stakeholders": ["product_manager", "cto", "design_director"]
                                },
                                "timeline": {
                                    "startDate": "2024-11-01T00:00:00Z",
                                    "endDate": "2025-03-31T23:59:59Z",
                                    "milestones": [
                                        {
                                            "name": "Design Phase",
                                            "dueDate": "2024-12-15T23:59:59Z",
                                            "completed": true
                                        },
                                        {
                                            "name": "Development Phase",
                                            "dueDate": "2025-02-28T23:59:59Z",
                                            "completed": false
                                        },
                                        {
                                            "name": "Testing & QA",
                                            "dueDate": "2025-03-15T23:59:59Z",
                                            "completed": false
                                        }
                                    ]
                                },
                                "budget": {
                                    "allocated": 250000,
                                    "spent": 87500,
                                    "remaining": 162500,
                                    "currency": "USD"
                                },
                                "technologies": ["React", "Node.js", "PostgreSQL", "Redis", "AWS"],
                                "tags": ["frontend", "backend", "database", "cloud", "responsive"]
                            }
                        ]
                    },
                    "statistics": {
                        "usage": {
                            "totalUsers": 15420,
                            "activeUsers": 8932,
                            "sessionsToday": 2341,
                            "averageSessionDuration": "12m 34s",
                            "topFeatures": [
                                { "name": "JSON Validation", "usage": 89.2 },
                                { "name": "Tree View", "usage": 76.8 },
                                { "name": "Search", "usage": 64.3 },
                                { "name": "Beautification", "usage": 58.9 },
                                { "name": "Export", "usage": 34.7 }
                            ]
                        },
                        "performance": {
                            "averageLoadTime": "1.2s",
                            "averageParseTime": "45ms",
                            "memoryUsage": "23.4MB",
                            "cpuUsage": "12%",
                            "uptime": "99.97%"
                        }
                    },
                    "metadata": {
                        "generated": "2025-01-08T15:30:00Z",
                        "generator": "JSON Viewer Demo Data Generator v1.2",
                        "schema": "https://schema.jsonviewer.com/demo/v1",
                        "checksums": {
                            "md5": "d41d8cd98f00b204e9800998ecf8427e",
                            "sha256": "e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855"
                        },
                        "compression": {
                            "original": "15.2KB",
                            "compressed": "4.8KB",
                            "ratio": "68.4%"
                        },
                        "validation": {
                            "isValid": true,
                            "lastChecked": "2025-01-08T15:30:00Z",
                            "validator": "JSON Schema Draft 2020-12"
                        }
                    }
                }

                this.jsonInput.setValue(JSON.stringify(sampleJSON, null, 2))
            }

            // Listen for component events
            inputContainer.addEventListener('jsonInput:validation', (event) => {
                const { result, isValid } = event.detail
                console.log('JSON Input validation:', { isValid, result })
            })

            inputContainer.addEventListener('jsonInput:clear', () => {
                console.log('JSON Input cleared')
            })

            inputContainer.addEventListener('jsonInput:beautify', (event) => {
                const { original, beautified } = event.detail
                console.log('JSON beautified')
            })

            inputContainer.addEventListener('jsonInput:minify', (event) => {
                const { original, minified } = event.detail
                console.log('JSON minified')
            })

            inputContainer.addEventListener('jsonInput:searchOpen', () => {
                console.log('Search opened')
            })

            inputContainer.addEventListener('jsonInput:searchClose', () => {
                console.log('Search closed')
            })

            inputContainer.addEventListener('jsonInput:fileDrop', (event) => {
                const { fileInfo, content, totalFiles } = event.detail
                console.log('File dropped successfully:', {
                    fileName: fileInfo?.name,
                    fileSize: fileInfo?.formattedSize,
                    totalFiles: totalFiles
                })
            })

            inputContainer.addEventListener('jsonInput:fileDropError', (event) => {
                const { error, errorType, fileInfo } = event.detail
                console.log('File drop failed:', {
                    error: error,
                    errorType: errorType,
                    fileName: fileInfo?.name
                })
            })

            console.log('JSON input component ready')
        }
    }

    /**
     * Set up tree viewer component
     */
    setupTreeViewer() {
        const treeContainer = document.querySelector('.json-tree-container')

        if (treeContainer) {
            console.log('Setting up JSON tree viewer component...')

            // Clear existing content
            treeContainer.innerHTML = ''

            // Initialize tree viewer component
            this.treeViewer = new JSONTreeViewer(treeContainer)

            // Set up tree navigation controls
            this.setupTreeNavigationControls()

            // Listen for tree viewer events
            treeContainer.addEventListener('nodeToggle', (event) => {
                const { node, expanded } = event.detail
                console.log(`Node ${expanded ? 'expanded' : 'collapsed'}:`, node.key)
            })

            treeContainer.addEventListener('nodeSelect', (event) => {
                const { node, path, value } = event.detail
                console.log('Node selected:', { key: node.key, path, value })
            })

            treeContainer.addEventListener('showKeyboardHelp', () => {
                if (this.keyboardHelpModal) {
                    this.keyboardHelpModal.show()
                }
            })

            treeContainer.addEventListener('treeExpandAll', () => {
                console.log('Tree expanded all')
            })

            treeContainer.addEventListener('treeCollapseAll', () => {
                console.log('Tree collapsed all')
            })

            treeContainer.addEventListener('treeExpandToLevel', (event) => {
                const { level } = event.detail
                console.log(`Tree expanded to level ${level}`)
            })

            treeContainer.addEventListener('nodeFocus', (event) => {
                const { node, path } = event.detail
                console.log('Node focused:', { key: node?.key, path })
            })

            // If there's already valid JSON, render it
            if (this.jsonInput && this.jsonInput.isValidJSON()) {
                const validationResult = this.jsonInput.getValidationResult()
                if (validationResult && validationResult.isValid) {
                    this.treeViewer.render(validationResult.data)
                }
            }

            console.log('JSON tree viewer component ready')
        }
    }

    /**
     * Update tree viewer with new JSON data
     */
    async updateTreeViewer(data) {
        if (this.treeViewer) {
            await this.treeViewer.render(data)
        }
    }

    /**
     * Clear tree viewer
     */
    clearTreeViewer() {
        if (this.treeViewer) {
            this.treeViewer.render(null)
        }
    }

    /**
     * Set up tree navigation controls in the output toolbar
     */
    setupTreeNavigationControls() {
        const outputToolbar = document.querySelector('.output-toolbar')

        if (!outputToolbar) return

        // Create navigation controls container
        const navControls = document.createElement('div')
        navControls.className = 'tree-nav-controls'

        // Expand All button
        const expandAllBtn = document.createElement('button')
        expandAllBtn.className = 'btn btn-secondary tree-nav-btn'
        expandAllBtn.innerHTML = '<span class="icon">⊞</span> <span class="btn-text">Expand All</span>'
        expandAllBtn.title = 'Expand all nodes (Ctrl+E)'
        expandAllBtn.addEventListener('click', () => {
            if (this.treeViewer) {
                this.treeViewer.expandAll()
            }
        })

        // Collapse All button
        const collapseAllBtn = document.createElement('button')
        collapseAllBtn.className = 'btn btn-secondary tree-nav-btn'
        collapseAllBtn.innerHTML = '<span class="icon">⊟</span> <span class="btn-text">Collapse All</span>'
        collapseAllBtn.title = 'Collapse all nodes (Ctrl+Shift+C)'
        collapseAllBtn.addEventListener('click', () => {
            if (this.treeViewer) {
                this.treeViewer.collapseAll()
            }
        })

        // Level selector
        const levelSelector = document.createElement('select')
        levelSelector.className = 'tree-level-selector'
        levelSelector.title = 'Expand to specific level (Ctrl+1-9)'

        // Add level options
        const defaultOption = document.createElement('option')
        defaultOption.value = ''
        defaultOption.textContent = 'Expand to level...'
        levelSelector.appendChild(defaultOption)

        for (let i = 1; i <= 9; i++) {
            const option = document.createElement('option')
            option.value = i.toString()
            option.textContent = `Level ${i}`
            levelSelector.appendChild(option)
        }

        levelSelector.addEventListener('change', (event) => {
            const level = parseInt(event.target.value)
            if (level && this.treeViewer) {
                this.treeViewer.expandToLevel(level)
            }
            // Reset selector
            event.target.value = ''
        })

        // Keyboard help button
        const keyboardHelpBtn = document.createElement('button')
        keyboardHelpBtn.className = 'btn btn-secondary tree-nav-btn keyboard-help-btn'
        keyboardHelpBtn.innerHTML = '<span class="icon">⌨</span> <span class="btn-text">Shortcuts</span>'
        keyboardHelpBtn.title = 'Show keyboard shortcuts (or press ? when tree is focused)'
        keyboardHelpBtn.setAttribute('aria-label', 'Show keyboard shortcuts help')

        keyboardHelpBtn.addEventListener('click', () => {
            if (this.keyboardHelpModal) {
                this.keyboardHelpModal.show()
            }
        })

        // Add all controls to container
        navControls.appendChild(expandAllBtn)
        navControls.appendChild(collapseAllBtn)
        navControls.appendChild(levelSelector)
        navControls.appendChild(keyboardHelpBtn)

        // Add to toolbar
        outputToolbar.appendChild(navControls)

        console.log('Tree navigation controls added to toolbar')
    }

    /**
     * Set up fullscreen functionality
     */
    setupFullscreen() {
        console.log('Setting up fullscreen functionality...')

        // Initialize fullscreen manager
        this.fullscreenManager = new FullscreenManager()

        // Add fullscreen buttons to toolbars
        this.fullscreenManager.addFullscreenButtons()

        // Listen for fullscreen events
        document.addEventListener('fullscreenEnter', (event) => {
            const { panelType } = event.detail
            console.log(`Entered fullscreen mode for ${panelType} panel`)
        })

        document.addEventListener('fullscreenExit', (event) => {
            const { panelType } = event.detail
            console.log(`Exited fullscreen mode for ${panelType} panel`)
        })

        console.log('Fullscreen functionality ready')
    }

    /**
     * Set up keyboard help modal
     */
    setupKeyboardHelpModal() {
        console.log('Setting up keyboard help modal...')

        // Initialize keyboard help modal
        this.keyboardHelpModal = new KeyboardHelpModal()

        console.log('Keyboard help modal ready')
    }

    /**
     * Set up mobile tab functionality
     */
    setupMobileTabs() {
        console.log('Setting up mobile tab functionality...')

        // Initialize mobile tab manager
        this.mobileTabManager = new MobileTabManager()

        // Listen for mobile panel changes to update components
        window.addEventListener('mobilePanelChanged', (event) => {
            const { activePanel, previousPanel } = event.detail
            console.log(`Mobile panel changed from ${previousPanel} to ${activePanel}`)
        })

        console.log('Mobile tab functionality ready')
    }

    /**
     * Get the current validation result
     */
    getValidationResult() {
        return this.jsonInput ? this.jsonInput.getValidationResult() : null
    }

    /**
     * Get the current JSON input value
     */
    getJSONValue() {
        return this.jsonInput ? this.jsonInput.getValue() : ''
    }

    /**
     * Set JSON input value
     */
    setJSONValue(value) {
        if (this.jsonInput) {
            this.jsonInput.setValue(value)
        }
    }

    /**
     * Check if current JSON is valid
     */
    isValidJSON() {
        return this.jsonInput ? this.jsonInput.isValidJSON() : false
    }

    /**
     * Set up feedback system
     */
    setupFeedback() {
        console.log('Setting up feedback system...')

        this.feedbackButton = new FeedbackButton({
            onFeedbackSubmitted: (data) => {
                console.log('📝 User feedback received:', data)
                // Here you could send to analytics or show a notification
                this.showNotification('Thank you for your feedback!', 'success', 3000)
            },
            onEmailSubmitted: (data) => {
                console.log('📧 User email submitted:', data)
                // Here you could add to mailing list
                this.showNotification('Thanks! We\'ll keep you updated.', 'success', 3000)
            }
        })

        console.log('Feedback system ready')
    }
}

// Initialize the application
const app = new JSONViewerApp()

// Export for potential external use
window.JSONViewerApp = app
window.JSONValidator = JSONValidator